import '../assets/NcReferenceWidget-Cg4xOL_8.css';
import { useElementSize, useIntersectionObserver } from "@vueuse/core";
import { ref, nextTick } from "vue";
import { RouterLink } from "vue-router";
import { N as NcButton } from "./NcButton-DAGirW_B.mjs";
import { r as register, l as t24, a as t } from "./_l10n-CAnZaLWU.mjs";
import { c as createElementId } from "./createElementId-DhjFt1I9.mjs";
import { d as destroyWidget, a as renderWidget, i as isWidgetRegistered, h as hasInteractiveView, b as isResizable, c as hasFullWidth } from "./widgets-XxirL-2f.mjs";
import { g as getRoute } from "./autolink-COrOqbJu.mjs";
import { n as normalizeComponent } from "./_plugin-vue2_normalizer-DU4iP6Vu.mjs";
register(t24);
const IDLE_TIMEOUT = 3 * 60 * 1e3;
const RESIZE_KEYBOARD_STEP = 10;
const _sfc_main = {
  name: "NcReferenceWidget",
  components: {
    NcButton
  },
  /* eslint vue/require-prop-comment: warn -- TODO: Add a proper doc block about what this props do */
  props: {
    reference: {
      type: Object,
      required: true
    },
    interactive: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default
      default: true
    },
    interactiveOptIn: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const widgetId = `nc-reference-widget-${createElementId()}`;
    const isVisible = ref(false);
    const widgetRoot = ref();
    const customWidget = ref();
    const { width } = useElementSize(widgetRoot);
    const { height: customWidgetHeight } = useElementSize(customWidget);
    useIntersectionObserver(widgetRoot, ([entry]) => {
      nextTick(() => {
        isVisible.value = entry.isIntersecting;
      });
    });
    return {
      width,
      isVisible,
      widgetRoot,
      customWidget,
      customWidgetHeight,
      widgetId,
      RESIZE_KEYBOARD_STEP
    };
  },
  data() {
    return {
      showInteractive: false,
      rendered: false,
      idleTimeout: null,
      isResizing: false,
      resizedHeight: null,
      resizeStartY: 0,
      resizeStartHeight: 0,
      resizeMinHeight: 100,
      resizeMaxHeight: window.innerHeight - 120
    };
  },
  computed: {
    isInteractive() {
      return !this.interactiveOptIn && this.interactive || this.showInteractive;
    },
    hasFullWidth() {
      return hasFullWidth(this.reference.richObjectType);
    },
    isResizable() {
      return isResizable(this.reference.richObjectType);
    },
    hasCustomWidget() {
      return isWidgetRegistered(this.reference.richObjectType);
    },
    customWidgetStyle() {
      if (!this.resizedHeight) {
        return null;
      }
      return { height: `${this.resizedHeight}px !important` };
    },
    ariaValueNow() {
      return Math.round(this.resizedHeight ?? this.customWidgetHeight);
    },
    hasInteractiveView() {
      return isWidgetRegistered(this.reference.richObjectType) && hasInteractiveView(this.reference.richObjectType);
    },
    noAccess() {
      return this.reference && !this.reference.accessible;
    },
    descriptionStyle() {
      if (this.numberOfLines === 0) {
        return {
          display: "none"
        };
      }
      const lineClamp = this.numberOfLines;
      return {
        lineClamp,
        webkitLineClamp: lineClamp
      };
    },
    numberOfLines() {
      const lineCountOffsets = [450, 550, 650, Infinity];
      return lineCountOffsets.findIndex((max) => this.width < max);
    },
    compactLink() {
      const link = this.reference.openGraphObject.link;
      if (!link) {
        return "";
      }
      if (link.startsWith("https://")) {
        return link.substring(8);
      }
      if (link.startsWith("http://")) {
        return link.substring(7);
      }
      return link;
    },
    route() {
      return getRoute(this.$router, this.reference.openGraphObject.link);
    },
    referenceWidgetLinkComponent() {
      return this.route ? RouterLink : "a";
    },
    referenceWidgetLinkProps() {
      return this.route ? { to: this.route } : { href: this.reference.openGraphObject.link, target: "_blank" };
    }
  },
  watch: {
    isVisible: {
      handler(val) {
        if (!val) {
          this.idleTimeout = setTimeout(() => {
            if (!this.isVisible) {
              this.destroyWidget();
            }
          }, IDLE_TIMEOUT);
          return;
        }
        if (this.idleTimeout) {
          clearTimeout(this.idleTimeout);
          this.idleTimeout = null;
        }
        if (!this.rendered) {
          this.renderWidget();
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimout = null;
    }
    this.stopResize();
    this.destroyWidget();
  },
  methods: {
    t,
    enableInteractive() {
      this.showInteractive = true;
      this.renderWidget();
    },
    renderWidget() {
      if (!this.$refs.customWidgetContent) {
        return;
      }
      if (this?.reference?.richObjectType === "open-graph") {
        return;
      }
      this.$refs.customWidgetContent.innerHTML = "";
      const widget = document.createElement("div");
      widget.style = "width: 100%;";
      this.$refs.customWidgetContent.appendChild(widget);
      this.$nextTick(() => {
        renderWidget(widget, {
          ...this.reference,
          interactive: this.isInteractive
        });
        this.rendered = true;
      });
    },
    destroyWidget() {
      if (this.rendered) {
        destroyWidget(this.reference.richObjectType, this.$el);
        this.rendered = false;
      }
    },
    initResizeLimits() {
      const widgetEl = this.$refs.customWidgetContent?.firstElementChild;
      if (widgetEl) {
        const computedStyle = window.getComputedStyle(widgetEl);
        const parsedMin = parseFloat(computedStyle.minHeight);
        const parsedMax = parseFloat(computedStyle.maxHeight);
        this.resizeMinHeight = parsedMin > 0 ? parsedMin : 100;
        this.resizeMaxHeight = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : window.innerHeight - 120;
      } else {
        this.resizeMinHeight = 100;
        this.resizeMaxHeight = window.innerHeight - 120;
      }
    },
    startResize(event) {
      if (!this.isResizable || !this.$refs.customWidget) {
        return;
      }
      this.initResizeLimits();
      this.isResizing = true;
      this.resizeStartY = event.clientY;
      this.resizeStartHeight = this.$refs.customWidget.getBoundingClientRect().height;
      window.addEventListener("pointermove", this.onResize);
      window.addEventListener("pointerup", this.stopResize);
    },
    onResizeKeydown(delta) {
      if (!this.isResizable || !this.$refs.customWidget) {
        return;
      }
      this.initResizeLimits();
      const currentHeight = this.resizedHeight ?? this.resizeStartHeight;
      const next = currentHeight + delta;
      this.resizedHeight = Math.min(this.resizeMaxHeight, Math.max(this.resizeMinHeight, next));
    },
    onResize(event) {
      if (!this.isResizing || !this.$refs.customWidget) {
        return;
      }
      const deltaY = event.clientY - this.resizeStartY;
      this.resizedHeight = Math.min(this.resizeMaxHeight, Math.max(this.resizeMinHeight, this.resizeStartHeight + deltaY));
    },
    stopResize() {
      if (!this.isResizing) {
        return;
      }
      this.isResizing = false;
      window.removeEventListener("pointermove", this.onResize);
      window.removeEventListener("pointerup", this.stopResize);
    }
  }
};
var _sfc_render = function render() {
  var _vm = this, _c = _vm._self._c;
  return _c("div", { ref: "widgetRoot", class: { "toggle-interactive": _vm.hasInteractiveView && !_vm.isInteractive } }, [_vm.reference && _vm.hasCustomWidget ? _c("div", { ref: "customWidget", staticClass: "widget-custom", class: {
    "full-width": _vm.hasFullWidth
  }, style: _vm.customWidgetStyle, attrs: { "id": _vm.widgetId } }, [_c("div", { ref: "customWidgetContent", staticClass: "widget-custom__content" }), _vm.isResizable ? _c("div", { staticClass: "widget-custom__resize-handle", attrs: { "role": "slider", "tabindex": "0", "aria-orientation": "vertical", "aria-label": _vm.t("Widget height"), "aria-valuenow": _vm.ariaValueNow, "aria-valuemin": _vm.resizeMinHeight, "aria-valuemax": _vm.resizeMaxHeight, "aria-controls": _vm.widgetId }, on: { "pointerdown": function($event) {
    $event.stopPropagation();
    $event.preventDefault();
    return _vm.startResize.apply(null, arguments);
  }, "keydown": [function($event) {
    if (!$event.type.indexOf("key") && _vm._k($event.keyCode, "up", 38, $event.key, ["Up", "ArrowUp"])) return null;
    $event.stopPropagation();
    $event.preventDefault();
    return _vm.onResizeKeydown(-_vm.RESIZE_KEYBOARD_STEP);
  }, function($event) {
    if (!$event.type.indexOf("key") && _vm._k($event.keyCode, "down", 40, $event.key, ["Down", "ArrowDown"])) return null;
    $event.stopPropagation();
    $event.preventDefault();
    return _vm.onResizeKeydown(_vm.RESIZE_KEYBOARD_STEP);
  }] } }) : _vm._e()]) : !_vm.noAccess && _vm.reference && _vm.reference.openGraphObject && !_vm.hasCustomWidget ? _c(_vm.referenceWidgetLinkComponent, _vm._b({ tag: "component", staticClass: "widget-default", attrs: { "rel": "noopener noreferrer" } }, "component", _vm.referenceWidgetLinkProps, false), [_vm.reference.openGraphObject.thumb ? _c("img", { staticClass: "widget-default--image", attrs: { "src": _vm.reference.openGraphObject.thumb } }) : _vm._e(), _c("div", { staticClass: "widget-default--details" }, [_c("p", { staticClass: "widget-default--name" }, [_vm._v(" " + _vm._s(_vm.reference.openGraphObject.name) + " ")]), _c("p", { staticClass: "widget-default--description", style: _vm.descriptionStyle }, [_vm._v(" " + _vm._s(_vm.reference.openGraphObject.description) + " ")]), _c("p", { staticClass: "widget-default--link" }, [_vm._v(" " + _vm._s(_vm.compactLink) + " ")])])]) : _vm._e(), _vm.interactiveOptIn && _vm.hasInteractiveView && !_vm.isInteractive ? _c("NcButton", { staticClass: "toggle-interactive--button", on: { "click": _vm.enableInteractive } }, [_vm._v(" " + _vm._s(_vm.t("Enable interactive view")) + " ")]) : _vm._e()], 1);
};
var _sfc_staticRenderFns = [];
var __component__ = /* @__PURE__ */ normalizeComponent(
  _sfc_main,
  _sfc_render,
  _sfc_staticRenderFns,
  false,
  null,
  "96e3ab0e"
);
const NcReferenceWidget = __component__.exports;
export {
  NcReferenceWidget as N
};
//# sourceMappingURL=NcReferenceWidget-CMq5QVka.mjs.map
